/**
 * Authentication Cache Utility
 * Caches user attributes and profile data to reduce API calls
 */
import { parseUserRoles } from './parseUserRoles';

const CACHE_KEYS = {
  USER_ATTRIBUTES: 'infhire_user_attributes',
  USER_PROFILE: 'infhire_user_profile',
  CACHE_TIMESTAMP: 'infhire_cache_timestamp'
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class AuthCache {
  constructor() {
    this.isSupported = this.checkStorageSupport();
  }

  checkStorageSupport() {
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      return true;
    } catch (e) {
      console.warn('SessionStorage not supported, auth cache disabled');
      return false;
    }
  }

  /**
   * Cache user attributes
   */
  cacheUserAttributes(attributes) {
    if (!this.isSupported || !attributes) return;

    try {
      const cacheData = {
        attributes,
        timestamp: Date.now()
      };
      sessionStorage.setItem(CACHE_KEYS.USER_ATTRIBUTES, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache user attributes:', e);
    }
  }

  /**
   * Get cached user attributes
   */
  getCachedUserAttributes() {
    if (!this.isSupported) return null;

    try {
      const cached = sessionStorage.getItem(CACHE_KEYS.USER_ATTRIBUTES);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // Check if cache is still valid
      if (Date.now() - cacheData.timestamp > CACHE_DURATION) {
        this.clearUserAttributes();
        return null;
      }

      return cacheData.attributes;
    } catch (e) {
      console.warn('Failed to retrieve cached user attributes:', e);
      return null;
    }
  }

  /**
   * Cache user profile data
   */
  cacheUserProfile(profile) {
    if (!this.isSupported || !profile) return;

    try {
      const cacheData = {
        profile,
        timestamp: Date.now()
      };
      sessionStorage.setItem(CACHE_KEYS.USER_PROFILE, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache user profile:', e);
    }
  }

  /**
   * Get cached user profile
   */
  getCachedUserProfile() {
    if (!this.isSupported) return null;

    try {
      const cached = sessionStorage.getItem(CACHE_KEYS.USER_PROFILE);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // Check if cache is still valid
      if (Date.now() - cacheData.timestamp > CACHE_DURATION) {
        this.clearUserProfile();
        return null;
      }

      return cacheData.profile;
    } catch (e) {
      console.warn('Failed to retrieve cached user profile:', e);
      return null;
    }
  }

  /**
   * Extract role information from attributes.
   * Delegates to parseUserRoles — the single source of truth for Cognito attribute parsing.
   */
  extractRoleInfo(attributes) {
    return parseUserRoles(attributes);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    if (!this.isSupported) return;

    try {
      sessionStorage.removeItem(CACHE_KEYS.USER_ATTRIBUTES);
      sessionStorage.removeItem(CACHE_KEYS.USER_PROFILE);
      sessionStorage.removeItem(CACHE_KEYS.CACHE_TIMESTAMP);
    } catch (e) {
      console.warn('Failed to clear auth cache:', e);
    }
  }

  /**
   * Clear cached user attributes
   */
  clearUserAttributes() {
    if (!this.isSupported) return;
    
    try {
      sessionStorage.removeItem(CACHE_KEYS.USER_ATTRIBUTES);
    } catch (e) {
      console.warn('Failed to clear cached user attributes:', e);
    }
  }

  /**
   * Clear cached user profile
   */
  clearUserProfile() {
    if (!this.isSupported) return;
    
    try {
      sessionStorage.removeItem(CACHE_KEYS.USER_PROFILE);
    } catch (e) {
      console.warn('Failed to clear cached user profile:', e);
    }
  }

  /**
   * Check if cache is valid
   */
  isCacheValid() {
    if (!this.isSupported) return false;

    const attributes = this.getCachedUserAttributes();
    return attributes !== null;
  }
}

// Export singleton instance
export const authCache = new AuthCache();
export default authCache;